/*
 * Collection ranking: the visitor's persona for the collection order.
 *
 * Nothing here runs on the way to the first paint. After the page has loaded
 * and the browser is idle, with tracking consent only (Shopify Customer
 * Privacy API), it asks the CRM app (/apps/wishlist/persona-context) at most
 * every 12 hours — or sooner when the products viewed on this device changed
 * and no persona was known — and stores the answer in the cart attributes
 * `_sks_p` (an opaque persona key) and `_sks_pg` (t = persona order,
 * c = control group). The next collection page is then rendered by Liquid in
 * the persona's order (sections/main-collection.liquid). Consent withdrawn:
 * the attributes are cleared.
 */
(function () {
  "use strict";

  var STATE_ID = "sks-persona-state";
  var CACHE_KEY = "sks:persona-ctx";
  var REFRESH_MS = 12 * 60 * 60 * 1000;
  var RETRY_MS = 5 * 60 * 1000;
  var RECENTLY_VIEWED_KEY = "theme:recently-viewed-products";
  var VISITOR_COOKIE = "product_intent_visitor_id";
  var TIMEOUT_MS = 4000;

  function state() {
    var el = document.getElementById(STATE_ID);
    if (!el) return null;
    try { return JSON.parse(el.textContent || "{}"); } catch (e) { return null; }
  }

  function privacy() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    function allowed(name) {
      try { return Boolean(api && typeof api[name] === "function" && api[name]()); } catch (e) { return false; }
    }
    return {
      ready: Boolean(api && typeof api.analyticsProcessingAllowed === "function"),
      a: allowed("analyticsProcessingAllowed"),
      m: allowed("marketingAllowed"),
    };
  }

  function visitorId() {
    try {
      var tracker = window.IntegratedProductIntent;
      var context = tracker && typeof tracker.getBrowserContext === "function" ? tracker.getBrowserContext() : null;
      if (context && context.visitorId) return String(context.visitorId);
    } catch (e) { /* the tracker is optional */ }
    var match = document.cookie.match(new RegExp("(?:^|; )" + VISITOR_COOKIE + "=([^;]*)"));
    var id = match ? decodeURIComponent(match[1]) : "";
    return /^[A-Za-z0-9_-]{1,128}$/.test(id) ? id : "";
  }

  function recentlyViewed() {
    try {
      var ids = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
      return Array.isArray(ids) ? ids.map(String).filter(function (id) { return /^[0-9]+$/.test(id); }).slice(0, 10) : [];
    } catch (e) {
      return [];
    }
  }

  function readCache() {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch (e) { return null; }
  }

  function writeCache(value) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch (e) { /* private mode */ }
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function setCart(current, key, group) {
    key = key || "";
    group = group || "";
    if ((current.p || "") === key && (current.g || "") === group) return;
    // keepalive: a tap that leaves the page (another product, Checkout) must not cut the write
    fetch(root() + "cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { _sks_p: key, _sks_pg: group } }),
    }).catch(function () { /* next page tries again */ });
  }

  function ask(status, visitor, viewed) {
    var params = new URLSearchParams({ a: status.a ? "1" : "0", m: status.m ? "1" : "0" });
    if (visitor) params.set("v", visitor);
    if (viewed.length) params.set("viewed", viewed.join(","));
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT_MS);
    return fetch("/apps/wishlist/persona-context?" + params.toString(), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller ? controller.signal : undefined,
    }).then(function (response) {
      clearTimeout(timer);
      if (!response.ok) throw new Error("status " + response.status);
      return response.json();
    });
  }

  function run() {
    var current = state();
    if (!current) return;
    var status = privacy();
    if (!status.ready) return;
    if (!status.a && !status.m) {
      setCart(current, "", "");
      return;
    }
    var viewed = recentlyViewed();
    var signature = viewed.slice(0, 5).join(",");
    var cache = readCache();
    var now = Date.now();
    var fresh = cache && now - cache.at < (cache.error ? RETRY_MS : REFRESH_MS)
      && (cache.key || cache.error || cache.viewed === signature);
    if (fresh) {
      if (!cache.error) setCart(current, cache.key, cache.group);
      return;
    }
    ask(status, visitorId(), viewed).then(function (answer) {
      var key = answer && answer.success && answer.key ? String(answer.key) : "";
      var group = key && (answer.group === "t" || answer.group === "c") ? answer.group : "";
      writeCache({ key: key, group: group, at: now, viewed: signature });
      setCart(current, key, group);
    }).catch(function () {
      writeCache({ key: cache && cache.key || "", group: cache && cache.group || "", at: now, viewed: signature, error: true });
    });
  }

  function whenIdle() {
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); };
    idle(function () { run(); }, { timeout: 5000 });
  }

  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
  // The consent banner answers later than the page: ask again once it has.
  document.addEventListener("visitorConsentCollected", function () { setTimeout(run, 500); });
})();
